/**
 * B2B registration EU VAT validation via Sufio VIES proxy.
 * Targets the Shopify Forms VAT field on the B2B registration page.
 *
 * Uses two Sufio app proxies:
 * - /apps/sufio/vat-check/     — live VIES validation (vat-check)
 * - /apps/sufio/customer-vat/  — tax-exempt registration (customer-vat, post-submit only)
 *
 * customer-vat is intentionally called only after Shopify Forms succeeds so Sufio
 * does not create the customer before the form links company main contact.
 */

/** @typedef {'empty' | 'validating' | 'valid' | 'invalid_format' | 'invalid_vat' | 'service_error' | 'registering_exemption'} VatValidationState */

/**
 * @typedef {Object} B2BVatValidationMessages
 * @property {string} validating
 * @property {string} valid
 * @property {string} invalidFormat
 * @property {string} invalid
 * @property {string} required
 * @property {string} serviceError
 * @property {string} emailRequired
 * @property {string} exemptionRegistering
 * @property {string} exemptionFailed
 * @property {string} exemptionPending
 * @property {string} formatHint
 */

/**
 * @typedef {Object} B2BVatValidationConfig
 * @property {string} vatFieldSelector
 * @property {string} emailFieldSelector
 * @property {string} vatCheckUrl
 * @property {string} customerVatUrl
 * @property {string} cssUrl
 * @property {number} debounceMs
 * @property {B2BVatValidationMessages} messages
 */

/** @typedef {{ valid: boolean, error?: boolean }} VatCheckResult */

/** @typedef {{ valid: boolean, error?: boolean }} CustomerVatResult */

/** @typedef {Window & { B2BVatValidation?: B2BVatValidationConfig, __b2bVatValidatorInitialized?: boolean }} B2BValidationWindow */

const BIND_OBSERVER_TIMEOUT_MS = 30_000;
const JSONP_TIMEOUT_MS = 15_000;
const POST_SUBMIT_EXEMPTION_INITIAL_DELAY_MS = 4_000;
const POST_SUBMIT_EXEMPTION_MAX_ATTEMPTS = 12;
const POST_SUBMIT_EXEMPTION_RETRY_MS = 3_000;

const FORM_SUCCESS_TEXT = [
  'success',
  'thank',
  'thanks',
  'submitted',
  'bedankt',
  'verzonden',
  'geregistreerd',
  'succesvol',
  'merci',
  'danke',
  'registriert',
];

const FALLBACK_VAT_SELECTORS = ['[data-testid="field-custom#vat"]', '[id="custom#vat"]'];

const FALLBACK_EMAIL_SELECTORS = [
  '[data-testid="field-email"]',
  '[data-testid*="field-email"]',
  'input[type="email"]',
  '[id="email"]',
];

const SUBMIT_SELECTORS = ['button[type="submit"]', '[data-testid*="submit"]'];

const SUCCESS_SELECTORS = [
  '[data-testid*="success"]',
  '[class*="success"]:not(.b2b-vat-validation__message):not(.b2b-vat-validation--valid)',
  '[role="status"]',
];

const MESSAGE_STATE_CLASSES = [
  'b2b-vat-validation__message--success',
  'b2b-vat-validation__message--error',
  'b2b-vat-validation__message--validating',
];

const INPUT_STATE_CLASSES = ['b2b-vat-validation--valid', 'b2b-vat-validation--invalid'];

/** @type {ReadonlySet<string>} */
const EU_COUNTRY_CODES = new Set([
  'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'EL', 'ES', 'FI', 'FR', 'GR',
  'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE',
  'SI', 'SK', 'XI',
]);

// --- VAT format helpers (pure) ---

/**
 * @param {string} value
 * @returns {string}
 */
function normalizeVat(value) {
  return value.trim().toUpperCase().replace(/[\s.\-]/g, '');
}

/**
 * @param {string} value
 * @returns {string}
 */
function toSufioVatNumber(value) {
  const normalized = normalizeVat(value);
  return normalized.startsWith('GR') ? `EL${normalized.slice(2)}` : normalized;
}

/**
 * @param {HTMLInputElement} input
 * @param {string} value
 * @param {() => void} [onBeforeDispatch]
 */
function applyNormalizedVatToInput(input, value, onBeforeDispatch) {
  const normalized = toSufioVatNumber(value);
  if (input.value === normalized) return;

  onBeforeDispatch?.();
  input.value = normalized;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isEuVatFormat(value) {
  const normalized = normalizeVat(value);
  if (normalized.length < 4) return false;

  const countryCode = normalized.slice(0, 2);
  const vatNumber = normalized.slice(2);

  return EU_COUNTRY_CODES.has(countryCode) && /^[A-Z0-9]{2,12}$/.test(vatNumber);
}

// --- Sufio API ---

/**
 * @param {string} endpoint
 * @param {string} callbackPrefix
 * @param {(data: Record<string, unknown>) => VatCheckResult | CustomerVatResult} parseResponse
 * @returns {Promise<VatCheckResult | CustomerVatResult>}
 */
function fetchSufioJsonp(endpoint, callbackPrefix, parseResponse) {
  /** @type {Record<string, ((data: Record<string, unknown>) => void) | undefined>} */
  const jsonpWindow =
    /** @type {Record<string, ((data: Record<string, unknown>) => void) | undefined>} */ (
      /** @type {unknown} */ (window)
    );

  return new Promise((resolve) => {
    const callbackName = `${callbackPrefix}_${Date.now()}`;
    const script = document.createElement('script');
    let settled = false;

    /** @param {VatCheckResult | CustomerVatResult} result */
    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeoutId);
      delete jsonpWindow[callbackName];
      script.remove();
      resolve(result);
    };

    const timeoutId = window.setTimeout(() => {
      finish({ valid: false, error: true });
    }, JSONP_TIMEOUT_MS);

    jsonpWindow[callbackName] = (data) => {
      finish(parseResponse(data ?? {}));
    };

    const separator = endpoint.includes('?') ? '&' : '?';
    script.src = `${endpoint}${separator}callback=${callbackName}`;
    script.onerror = () => finish({ valid: false, error: true });
    document.head.appendChild(script);
  });
}

/**
 * @param {string} number
 * @param {string} url
 * @param {AbortSignal} [signal]
 * @returns {Promise<VatCheckResult>}
 */
async function checkVatWithSufio(number, url, signal) {
  const endpoint = `${url}?number=${encodeURIComponent(number)}`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });

    if (response.ok) {
      const data = await response.json();
      return { valid: Boolean(data.result) };
    }
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw error;
    }
  }

  return /** @type {Promise<VatCheckResult>} */ (
    fetchSufioJsonp(endpoint, 'sufioVatCheck', (data) => ({
      valid: Boolean(data.result),
    }))
  );
}

/**
 * @param {string} email
 * @param {string} vatNo
 * @param {string} url
 * @returns {Promise<CustomerVatResult>}
 */
async function registerCustomerVatWithSufio(email, vatNo, url) {
  const params = new URLSearchParams({ email, vat_no: vatNo });
  const endpoint = `${url}?${params.toString()}`;

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.ok) {
      const data = await response.json();
      return { valid: Boolean(data.vat_no_valid) };
    }
  } catch {
    // Fall through to JSONP
  }

  return /** @type {Promise<CustomerVatResult>} */ (
    fetchSufioJsonp(endpoint, 'sufioCustomerVat', (data) => ({
      valid: Boolean(data.vat_no_valid),
    }))
  );
}

// --- Shadow DOM helpers ---

/**
 * @param {ParentNode} root
 * @param {string} selector
 * @returns {Element | null}
 */
function querySelectorDeep(root, selector) {
  if (root instanceof Element || root instanceof DocumentFragment) {
    const direct = root.querySelector(selector);
    if (direct) return direct;
  }

  const scope = root instanceof Document ? root.body : root;
  if (!(scope instanceof Element) && !(scope instanceof DocumentFragment)) return null;

  for (const element of scope.querySelectorAll('*')) {
    if (!element.shadowRoot) continue;

    const match = querySelectorDeep(element.shadowRoot, selector);
    if (match) return match;
  }

  return null;
}

/**
 * @param {ParentNode} root
 * @param {string[]} selectors
 * @returns {HTMLInputElement | null}
 */
function resolveInput(root, selectors) {
  for (const selector of selectors) {
    const match = root.querySelector(selector) ?? querySelectorDeep(root, selector);
    if (match instanceof HTMLInputElement) return match;
  }

  return null;
}

/**
 * @param {ParentNode} searchRoot
 * @returns {HTMLButtonElement | null}
 */
function resolveSubmitButton(searchRoot) {
  for (const selector of SUBMIT_SELECTORS) {
    const match = searchRoot.querySelector(selector) ?? querySelectorDeep(searchRoot, selector);
    if (match instanceof HTMLButtonElement) return match;
  }

  return null;
}

/**
 * @param {ShadowRoot} shadowRoot
 * @param {string} cssUrl
 */
function attachShadowStylesheet(shadowRoot, cssUrl) {
  if (!cssUrl || shadowRoot.querySelector('link[data-b2b-vat-validation]')) return;

  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = cssUrl;
  link.setAttribute('data-b2b-vat-validation', '');
  shadowRoot.prepend(link);
}

/**
 * @param {B2BVatValidationConfig} config
 * @returns {string[]}
 */
function getVatSelectors(config) {
  return [...new Set([config.vatFieldSelector, ...FALLBACK_VAT_SELECTORS])];
}

/**
 * @param {B2BVatValidationConfig} config
 * @returns {string[]}
 */
function getEmailSelectors(config) {
  return [...new Set([config.emailFieldSelector, ...FALLBACK_EMAIL_SELECTORS])];
}

// --- Validator ---

class B2BVatValidator {
  /** @type {B2BVatValidationConfig} */
  #config;

  /** @type {VatValidationState} */
  #state = 'empty';

  /** @type {number | null} */
  #debounceTimer = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number} */
  #validationGeneration = 0;

  /** @type {boolean} */
  #bound = false;

  /** @type {boolean} */
  #allowNativeSubmit = false;

  /** @type {boolean} */
  #exemptionRegistered = false;

  /** @type {boolean} */
  #exemptionRetryStarted = false;

  /** @type {boolean} */
  #formSubmitted = false;

  /** @type {string} */
  #submittedEmail = '';

  /** @type {string} */
  #submittedVat = '';

  /** @type {boolean} */
  #skipNextInput = false;

  /** @type {HTMLInputElement | null} */
  #vatInput = null;

  /** @type {HTMLFormElement | null} */
  #form = null;

  /** @type {HTMLButtonElement | null} */
  #submitButton = null;

  /** @type {ParentNode | null} */
  #formRoot = null;

  /** @type {HTMLElement | null} */
  #messageEl = null;

  /** @type {HTMLElement | null} */
  #messageText = null;

  /** @type {HTMLElement | null} */
  #messageSpinner = null;

  /** @type {HTMLElement | null} */
  #hintEl = null;

  /** @type {string} */
  #messageId;

  /** @type {string} */
  #hintId;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #observerTimeout = null;

  /** @type {MutationObserver | null} */
  #observer = null;

  /** @type {MutationObserver | null} */
  #successObserver = null;

  /** @type {(event: Event) => void} */
  #onInput;

  /** @type {(event: Event) => void} */
  #onBlur;

  /** @type {(event: Event) => void} */
  #onSubmitAttempt;

  /**
   * @param {B2BVatValidationConfig} config
   */
  constructor(config) {
    this.#config = config;
    this.#messageId = `b2b-vat-validation-message-${Math.random().toString(36).slice(2, 9)}`;
    this.#hintId = `b2b-vat-validation-hint-${Math.random().toString(36).slice(2, 9)}`;
    this.#onInput = this.#handleInput.bind(this);
    this.#onBlur = this.#handleBlur.bind(this);
    this.#onSubmitAttempt = this.#handleSubmitAttempt.bind(this);
  }

  init() {
    const root = document.querySelector('#MainContent');
    if (!root) return;

    if (this.#tryBind(root)) return;

    this.#observer = new MutationObserver(() => {
      if (this.#tryBind(root)) {
        this.#observer?.disconnect();
        this.#observer = null;
      }
    });

    this.#observer.observe(root, { childList: true, subtree: true });

    this.#observerTimeout = window.setTimeout(() => {
      this.#observer?.disconnect();
      this.#observer = null;
    }, BIND_OBSERVER_TIMEOUT_MS);
  }

  /**
   * @param {ParentNode} root
   * @returns {boolean}
   */
  #tryBind(root) {
    if (this.#bound) return true;

    const vatInput = resolveInput(root, getVatSelectors(this.#config));
    if (!vatInput) return false;

    this.#vatInput = vatInput;
    this.#form = vatInput.closest('form');

    const inputRoot = vatInput.getRootNode();
    this.#formRoot = inputRoot instanceof ShadowRoot ? inputRoot : root;

    this.#submitButton =
      resolveSubmitButton(this.#formRoot) ?? resolveSubmitButton(root);

    if (inputRoot instanceof ShadowRoot) {
      attachShadowStylesheet(inputRoot, this.#config.cssUrl);
    }

    this.#createUi();
    this.#attachListeners();
    this.#observeFormSuccess();
    this.#bound = true;
    this.#setState('empty');

    return true;
  }

  #createUi() {
    if (!this.#vatInput) return;

    const insertTarget =
      this.#vatInput.closest('[class*="field"]') ?? this.#vatInput.parentElement ?? this.#vatInput;

    this.#hintEl = document.createElement('p');
    this.#hintEl.id = this.#hintId;
    this.#hintEl.className = 'b2b-vat-validation__hint';
    this.#hintEl.textContent = this.#config.messages.formatHint;
    insertTarget.insertAdjacentElement('afterend', this.#hintEl);

    this.#messageEl = document.createElement('div');
    this.#messageEl.id = this.#messageId;
    this.#messageEl.className = 'b2b-vat-validation__message hidden';
    this.#messageEl.setAttribute('role', 'alert');

    this.#messageSpinner = document.createElement('span');
    this.#messageSpinner.className = 'b2b-vat-validation__spinner hidden';
    this.#messageSpinner.setAttribute('aria-hidden', 'true');

    this.#messageText = document.createElement('span');
    this.#messageText.className = 'b2b-vat-validation__text';

    this.#messageEl.append(this.#messageSpinner, this.#messageText);
    this.#hintEl.insertAdjacentElement('afterend', this.#messageEl);

    this.#vatInput.setAttribute('aria-describedby', this.#hintId);
  }

  #attachListeners() {
    if (!this.#vatInput) return;

    this.#vatInput.addEventListener('input', this.#onInput);
    this.#vatInput.addEventListener('blur', this.#onBlur);
    this.#form?.addEventListener('submit', this.#onSubmitAttempt, true);
    document.addEventListener('click', this.#onSubmitAttempt, true);
  }

  #observeFormSuccess() {
    if (!this.#formRoot) return;

    this.#successObserver = new MutationObserver(() => {
      if (
        !this.#formSubmitted ||
        !this.#hasFormSucceeded() ||
        this.#exemptionRegistered ||
        this.#exemptionRetryStarted
      ) {
        return;
      }

      const email = this.#submittedEmail;
      const vat = this.#submittedVat;
      if (!email || !vat) return;

      this.#exemptionRetryStarted = true;
      this.#registerExemptionAfterSubmit(email, vat);
    });

    this.#successObserver.observe(this.#formRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'data-testid', 'role'],
    });
  }

  /**
   * @returns {boolean}
   */
  #hasFormSucceeded() {
    if (!this.#formRoot) return false;

    for (const selector of SUCCESS_SELECTORS) {
      const match =
        (this.#formRoot instanceof Document || this.#formRoot instanceof ShadowRoot || this.#formRoot instanceof Element
          ? this.#formRoot.querySelector(selector)
          : null) ?? querySelectorDeep(this.#formRoot, selector);

      if (match instanceof HTMLElement && match.offsetParent !== null) {
        const text = match.textContent?.toLowerCase() ?? '';
        if (FORM_SUCCESS_TEXT.some((keyword) => text.includes(keyword))) {
          return true;
        }
      }
    }

    return false;
  }

  /**
   * @param {Event} event
   */
  #handleSubmitAttempt(event) {
    if (this.#allowNativeSubmit) return;

    if (this.#state === 'valid') {
      if (event.type === 'click' && !this.#isSubmitInteraction(event.target)) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.#submitFormWhenReady();
      return;
    }

    if (event.type === 'click' && !this.#isSubmitInteraction(event.target)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const message =
      this.#state === 'empty' ? this.#config.messages.required : this.#getMessageForState(this.#state);

    this.#updateUi(message, this.#state === 'empty' ? 'invalid_format' : this.#state);
  }

  #submitFormWhenReady() {
    const email = this.#getEmailValue();
    const vat = this.#getVatValue();

    if (!email) {
      this.#updateUi(this.#config.messages.emailRequired, 'invalid_format');
      return;
    }

    if (!vat) {
      this.#updateUi(this.#config.messages.required, 'invalid_format');
      return;
    }

    if (this.#vatInput) {
      applyNormalizedVatToInput(this.#vatInput, vat, () => {
        this.#skipNextInput = true;
      });
    }

    const normalizedVat = toSufioVatNumber(vat);

    this.#submittedEmail = email;
    this.#submittedVat = normalizedVat;
    this.#formSubmitted = true;
    this.#exemptionRetryStarted = false;
    this.#proceedWithNativeSubmit();
  }

  /**
   * @param {string} email
   * @param {string} vat
   */
  async #registerExemptionAfterSubmit(email, vat) {
    this.#setState('registering_exemption');

    await new Promise((resolve) => window.setTimeout(resolve, POST_SUBMIT_EXEMPTION_INITIAL_DELAY_MS));

    for (let attempt = 0; attempt < POST_SUBMIT_EXEMPTION_MAX_ATTEMPTS; attempt++) {
      if (this.#exemptionRegistered) return;

      const result = await registerCustomerVatWithSufio(
        email,
        toSufioVatNumber(vat),
        this.#config.customerVatUrl
      );

      if (result.valid) {
        this.#exemptionRegistered = true;
        this.#setState('valid');
        return;
      }

      if (attempt < POST_SUBMIT_EXEMPTION_MAX_ATTEMPTS - 1) {
        await new Promise((resolve) => window.setTimeout(resolve, POST_SUBMIT_EXEMPTION_RETRY_MS));
      }
    }

    if (!this.#exemptionRegistered) {
      this.#setState('valid');
      this.#updateUi(this.#config.messages.exemptionFailed, 'invalid_vat');
    }
  }

  #proceedWithNativeSubmit() {
    this.#allowNativeSubmit = true;

    if (this.#submitButton) {
      this.#submitButton.disabled = false;
      this.#submitButton.removeAttribute('aria-disabled');
      this.#submitButton.click();
      return;
    }

    this.#form?.requestSubmit();
  }

  /**
   * @returns {string}
   */
  #getEmailValue() {
    if (!this.#formRoot) return '';

    const emailInput = resolveInput(this.#formRoot, getEmailSelectors(this.#config));
    return emailInput?.value.trim() ?? '';
  }

  /**
   * @returns {string}
   */
  #getVatValue() {
    const raw = this.#vatInput?.value.trim() ?? '';
    return this.#state === 'valid' ? toSufioVatNumber(raw) : raw;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {boolean}
   */
  #isSubmitInteraction(target) {
    if (!(target instanceof Node) || !this.#submitButton) return false;

    return (
      target === this.#submitButton ||
      this.#submitButton.contains(target) ||
      (target instanceof Element && target.closest('button[type="submit"]') === this.#submitButton)
    );
  }

  /**
   * @param {Event} event
   */
  #handleInput(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (this.#skipNextInput) {
      this.#skipNextInput = false;
      return;
    }

    this.#allowNativeSubmit = false;
    this.#formSubmitted = false;
    this.#exemptionRegistered = false;
    this.#exemptionRetryStarted = false;
    this.#submittedEmail = '';
    this.#submittedVat = '';

    const { value } = event.target;

    if (this.#debounceTimer) window.clearTimeout(this.#debounceTimer);

    this.#debounceTimer = window.setTimeout(() => {
      this.#validate(value);
    }, this.#config.debounceMs);
  }

  /**
   * @param {Event} event
   */
  #handleBlur(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    if (this.#debounceTimer) {
      window.clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }

    this.#validate(event.target.value);
  }

  /**
   * @param {string} value
   */
  async #validate(value) {
    const trimmed = value.trim();

    if (!trimmed) {
      this.#setState('empty');
      return;
    }

    if (!isEuVatFormat(trimmed)) {
      this.#setState('invalid_format');
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const generation = ++this.#validationGeneration;
    const signal = this.#abortController.signal;

    this.#setState('validating');

    try {
      const result = await checkVatWithSufio(toSufioVatNumber(trimmed), this.#config.vatCheckUrl, signal);

      if (generation !== this.#validationGeneration || signal.aborted) return;

      if (result.error) {
        this.#setState('service_error');
        return;
      }

      if (result.valid && this.#vatInput) {
        applyNormalizedVatToInput(this.#vatInput, trimmed, () => {
          this.#skipNextInput = true;
        });
      }

      this.#setState(result.valid ? 'valid' : 'invalid_vat');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      if (generation !== this.#validationGeneration) return;
      this.#setState('service_error');
    }
  }

  /**
   * @param {VatValidationState} state
   */
  #setState(state) {
    this.#state = state;
    this.#updateSubmitButton();
    this.#updateInputAttributes(state);
    this.#updateUi(this.#getMessageForState(state), state);
  }

  /**
   * @param {VatValidationState} state
   * @returns {string}
   */
  #getMessageForState(state) {
    const { messages } = this.#config;

    switch (state) {
      case 'validating':
        return messages.validating;
      case 'registering_exemption':
        return messages.exemptionRegistering;
      case 'valid':
        return messages.valid;
      case 'invalid_format':
        return messages.invalidFormat;
      case 'invalid_vat':
        return messages.invalid;
      case 'service_error':
        return messages.serviceError;
      default:
        return '';
    }
  }

  /**
   * @param {string} message
   * @param {VatValidationState} state
   */
  #updateUi(message, state) {
    if (!this.#messageEl || !this.#messageText || !this.#vatInput) return;

    this.#messageEl.classList.remove('hidden', ...MESSAGE_STATE_CLASSES);
    this.#vatInput.classList.remove(...INPUT_STATE_CLASSES);

    if (state === 'empty') {
      this.#messageEl.classList.add('hidden');
      this.#messageText.textContent = '';
      this.#messageSpinner?.classList.add('hidden');
      this.#vatInput.removeAttribute('aria-invalid');
      this.#vatInput.setAttribute('aria-describedby', this.#hintId);
      return;
    }

    this.#messageText.textContent = message;

    if (state === 'validating' || state === 'registering_exemption') {
      this.#messageEl.classList.add('b2b-vat-validation__message--validating');
      this.#messageSpinner?.classList.remove('hidden');
      this.#vatInput.setAttribute('aria-describedby', `${this.#hintId} ${this.#messageId}`);
      return;
    }

    this.#messageSpinner?.classList.add('hidden');

    if (state === 'valid') {
      this.#messageEl.classList.add('b2b-vat-validation__message--success');
      this.#vatInput.classList.add('b2b-vat-validation--valid');
      this.#vatInput.setAttribute('aria-invalid', 'false');
      this.#vatInput.setAttribute('aria-describedby', `${this.#hintId} ${this.#messageId}`);
      return;
    }

    this.#messageEl.classList.add('b2b-vat-validation__message--error');
    this.#vatInput.classList.add('b2b-vat-validation--invalid');
    this.#vatInput.setAttribute('aria-invalid', 'true');
    this.#vatInput.setAttribute('aria-describedby', `${this.#hintId} ${this.#messageId}`);
  }

  /**
   * @param {VatValidationState} state
   */
  #updateInputAttributes(state) {
    if (!this.#vatInput) return;

    if (state === 'valid') {
      this.#vatInput.dataset.b2bVatValidated = 'true';
      return;
    }

    delete this.#vatInput.dataset.b2bVatValidated;
  }

  #updateSubmitButton() {
    if (!this.#submitButton) return;

    const isSubmittable = this.#state === 'valid';
    this.#submitButton.disabled = !isSubmittable;
    this.#submitButton.setAttribute('aria-disabled', String(!isSubmittable));
  }

  destroy() {
    if (this.#debounceTimer) window.clearTimeout(this.#debounceTimer);
    if (this.#observerTimeout) window.clearTimeout(this.#observerTimeout);

    this.#observer?.disconnect();
    this.#successObserver?.disconnect();
    this.#abortController?.abort();

    this.#vatInput?.removeEventListener('input', this.#onInput);
    this.#vatInput?.removeEventListener('blur', this.#onBlur);
    this.#form?.removeEventListener('submit', this.#onSubmitAttempt, true);
    document.removeEventListener('click', this.#onSubmitAttempt, true);

    this.#messageEl?.remove();
    this.#hintEl?.remove();

    this.#bound = false;
  }
}

function initB2BVatValidation() {
  /** @type {B2BValidationWindow} */
  const b2bWindow = window;
  const { B2BVatValidation: config } = b2bWindow;
  if (!config || b2bWindow.__b2bVatValidatorInitialized) return;

  b2bWindow.__b2bVatValidatorInitialized = true;
  new B2BVatValidator(config).init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initB2BVatValidation);
} else {
  initB2BVatValidation();
}
